import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth } from './auth/RequireAuth';
import { RequireRole } from './auth/RequireRole';
import { AnnouncementHost } from './components/announcements/AnnouncementHost';
import { SessionGuard } from './auth/SessionGuard';
import { AskHy } from './components/chat/AskHy';
import { FeedbackButton } from './components/feedback/FeedbackButton';
import { DepletionsView } from './components/DepletionsView';
import { LandingPage } from './pages/LandingPage';
import { MarketingBlogPage } from './pages/MarketingBlogPage';
import { MarketingCompetitorsPage } from './pages/MarketingCompetitorsPage';
import { MarketingNewsPage } from './pages/MarketingNewsPage';
import { MarketingPage } from './pages/MarketingPage';
import { MarketingPatternBuilderPage } from './pages/MarketingPatternBuilderPage';
import { MarketingPatternsPage } from './pages/MarketingPatternsPage';
import { NotesPage } from './pages/NotesPage';
import { ProfilePage } from './pages/ProfilePage';
import { SalesDashboardPage } from './pages/SalesDashboardPage';
import { UserGuidePage } from './pages/UserGuidePage';
import { WhatsNewPage } from './pages/WhatsNewPage';
import { AdminFieldPage } from './pages/admin/AdminFieldPage';
import { AdminRepViewAsPage } from './pages/admin/AdminRepViewAsPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AnnouncementsPage } from './pages/admin/AnnouncementsPage';
import { DataUploadsPage } from './pages/admin/DataUploadsPage';
import { AskAbhiAdminPage } from './pages/admin/AskAbhiAdminPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { RolesPage } from './pages/admin/RolesPage';
import { UsersPage } from './pages/admin/UsersPage';
import { FieldAccountDetailPage } from './pages/field/FieldAccountDetailPage';
import { FieldPage } from './pages/field/FieldPage';
import { ChangePasswordPage } from './pages/auth/ChangePasswordPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { LoginPage } from './pages/auth/LoginPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { SignupPage } from './pages/auth/SignupPage';
import { FEATURE_COMPETITOR_WATCH } from './config/flags';

/**
 * Top-level routes.
 *
 * Public:
 *   /login, /signup, /forgot-password, /reset-password
 *   (and `/auth/...` aliases of the same — the backend's email
 *    links use the `/auth/...` form, matching the file paths
 *    under `pages/auth/`; the flat URLs are kept for backwards
 *    compatibility with in-app `<Link>`s.)
 *
 * Authenticated (any role):
 *   /, /change-password
 *
 * Role-gated:
 *   /sales/depletions   → `depletions`
 *   /marketing/**       → `marketing`
 */
function App() {
  return (
    <>
      {/* Announcements. Mounted here rather than in SiteLayout so it
          also covers the landing page — which builds its own layout, and
          is where a user lands straight after signing in. Renders
          nothing unless there is a live announcement this person has not
          dismissed. */}
      <SessionGuard />
      <AnnouncementHost />

      {/* Floating Feedback pill, anchored bottom-right.  Renders for
          authenticated users on every page; the component opts itself
          out when no user is signed in. */}
      <FeedbackButton />

      {/* Ask ABHI assistant pill + drawer, stacked above Feedback. Renders
          only when /api/chat/status says this user may use it. */}
      <AskHy />
      <Routes>
        {/* Public auth flows.  Each is reachable at both the flat
            path (used by in-app Links and the existing UserMenu sign-
            out redirect) and the `/auth/...` path (used by the email
            links from the backend — see settings.frontend_reset_url). */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

        {/* Authenticated but not role-gated */}
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
        {/* Notes are person-to-person and can be addressed to ANY
            active user, so this deliberately has no role gate — a
            non-rep who receives one still needs somewhere to read it.
            Reps reach the same component via a tab in their own
            workspace. */}
        <Route
          path="/notes"
          element={
            <RequireAuth>
              <NotesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/change-password"
          element={
            <RequireAuth>
              <ChangePasswordPage />
            </RequireAuth>
          }
        />

        {/* Authenticated home */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <LandingPage />
            </RequireAuth>
          }
        />

        {/* User Guide — onboarding manual for Depletions, Regional
            Sales, and Regional Sales Oversight.  No role gate: the
            guide describes sections a reader may not yet have access
            to, which is exactly what a new joiner needs.  `?print=1`
            renders the printable/PDF layout. */}
        <Route
          path="/user-guide"
          element={
            <RequireAuth>
              <UserGuidePage />
            </RequireAuth>
          }
        />

        {/* What's New / release notes — internal changelog */}
        <Route
          path="/whats-new"
          element={
            <RequireAuth>
              <WhatsNewPage />
            </RequireAuth>
          }
        />

        {/* Sales section — role-per-tab gating */}
        <Route
          path="/sales"
          element={
            <RequireAuth>
              <SalesDashboardPage />
            </RequireAuth>
          }
        >
          {/* Distribution was retired at HY's request, and its views have
              since been deleted.  The redirect stays: `/sales` and any
              stale `/sales/distribution` bookmark both land on Depletions
              rather than a dead route. */}
          <Route index element={<Navigate to="depletions" replace />} />
          <Route path="distribution" element={<Navigate to="/sales/depletions" replace />} />
          <Route
            path="depletions"
            element={
              <RequireRole role="depletions">
                <DepletionsView />
              </RequireRole>
            }
          />
        </Route>

        {/* Admin section — admin role required for everything */}
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <RequireRole role="admin">
                <AdminLayout />
              </RequireRole>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<UsersPage />} />
          {/* /admin/pending used to be its own tab; it now folds into
              the Users list as a status filter.  Keep the route as a
              redirect so any bookmark / email link still works. */}
          <Route path="pending" element={<Navigate to="/admin/users?status=pending" replace />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="data-uploads" element={<DataUploadsPage />} />
          <Route path="ask-abhi" element={<AskAbhiAdminPage />} />
        </Route>

        {/* Regional Sales Oversight — standalone admin surface.
            URL kept under /admin/* so the home page card link
            stays stable, but the page renders its own SiteLayout
            instead of nesting under AdminLayout (which is scoped
            to Users / Roles / Audit Trail management). */}
        <Route
          path="/admin/regional-sales"
          element={
            <RequireAuth>
              <RequireRole role="admin">
                <AdminFieldPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/* Admin "view / act as rep" — renders the rep's own FieldPage in
            impersonation mode (see AdminRepViewAsPage). */}
        <Route
          path="/admin/regional-sales/reps/:userId/view"
          element={
            <RequireAuth>
              <RequireRole role="admin">
                <AdminRepViewAsPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/* Regional Sales section — sales-rep CRM. URL renamed
            from /field to /regional-sales to align with the
            customer-facing terminology; internal role + API still
            use `field_rep` and `/api/field/*` to avoid backend
            churn. */}
        <Route
          path="/regional-sales"
          element={
            <RequireAuth>
              <RequireRole role="field_rep">
                <FieldPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/regional-sales/accounts/:accountId"
          element={
            <RequireAuth>
              <RequireRole role="field_rep">
                <FieldAccountDetailPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/* Marketing section — single role for the whole subtree */}
        <Route
          path="/marketing"
          element={
            <RequireAuth>
              <RequireRole role="marketing">
                <MarketingPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/marketing/patterns"
          element={
            <RequireAuth>
              <RequireRole role="marketing">
                <MarketingPatternsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/marketing/pattern-builder"
          element={
            <RequireAuth>
              <RequireRole role="marketing">
                <MarketingPatternBuilderPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* Route is omitted entirely when the feature is hidden, so a typed
            URL falls through to the catch-all instead of rendering a page
            whose API returns 404. */}
        {FEATURE_COMPETITOR_WATCH ? (
          <Route
            path="/marketing/competitors"
            element={
              <RequireAuth>
                <RequireRole role="marketing">
                  <MarketingCompetitorsPage />
                </RequireRole>
              </RequireAuth>
            }
          />
        ) : null}
        <Route
          path="/marketing/news"
          element={
            <RequireAuth>
              <RequireRole role="marketing">
                <MarketingNewsPage />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/marketing/blog"
          element={
            <RequireAuth>
              <RequireRole role="marketing">
                <MarketingBlogPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/* Fallback */}
        <Route
          path="*"
          element={
            <RequireAuth>
              <LandingPage />
            </RequireAuth>
          }
        />
      </Routes>
    </>
  );
}

export default App;
